/**
 * React hook wrapping the new Gemini Live GD websocket flow.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import gdApi, { GdStartResponse } from '../api/gdApi';
import GdOrchestrationService, { GdMessage, GdOrchestrationCallbacks, GdParticipant } from '../services/GdOrchestrationService';

export interface UseGdOrchestrationProps {
  onStatus: (status: string) => void;
  onError: (error: string) => void;
}

export interface GdState {
  connected: boolean;
  reconnecting: boolean;
  sessionId: string | null;
  topic: string | null; // Backend should return topic title as string
  topicDescription: string | null;
  participants: GdParticipant[];
  currentSpeaker: string | null;
  round: number | null;
  isUserTurn: boolean;
  messages: GdMessage[];
  completed: boolean;
  userName: string | null;
}

const initialState: GdState = {
  connected: false,
  reconnecting: false,
  sessionId: null,
  topic: null,
  topicDescription: null,
  participants: [],
  currentSpeaker: null,
  round: null,
  isUserTurn: false,
  messages: [],
  completed: false,
  userName: null,
};

export const useGdOrchestration = ({ onStatus, onError }: UseGdOrchestrationProps) => {
  const [state, setState] = useState<GdState>(initialState);
  const serviceRef = useRef<GdOrchestrationService | null>(null);
  const sessionRef = useRef<GdStartResponse | null>(null);

  const ensureService = useCallback(() => {
    if (serviceRef.current) return serviceRef.current;

    const callbacks: GdOrchestrationCallbacks = {
      onStatus,
      onParticipants: (participants) => setState((prev) => ({ ...prev, participants })),
      onCurrentSpeaker: (speaker) => setState((prev) => ({ ...prev, currentSpeaker: speaker })),
      onUserTurn: (isUserTurn) => setState((prev) => ({ ...prev, isUserTurn })),
      onMessage: (message) => setState((prev) => ({ ...prev, messages: [...prev.messages, message] })),
      onRound: (round) => setState((prev) => ({ ...prev, round })),
      onDiscussionComplete: () => setState((prev) => ({ ...prev, completed: true, isUserTurn: false })),
      onConnected: () => setState((prev) => ({ ...prev, connected: true, reconnecting: false })),
      onDisconnected: () => setState((prev) => {
        // Only mark as reconnecting if we had a session and it's not completed
        if (prev.sessionId && !prev.completed) {
          return { ...prev, connected: false, reconnecting: true };
        }
        return { ...prev, connected: false, reconnecting: false };
      }),
      onError,
    };

    serviceRef.current = new GdOrchestrationService(callbacks);
    return serviceRef.current;
  }, [onError, onStatus]);

  const startSession = useCallback(
    async (params: { token: string; topic_id?: string; description?: string }) => {
      const { token, topic_id, description } = params;
      onStatus('Starting discussion...');
      try {
        const session = await gdApi.start(token, topic_id, description);
        sessionRef.current = session;
        const service = ensureService();

        setState((prev) => ({
          ...prev,
          connected: false, // Will be set to true by onConnected callback when WS opens
          reconnecting: false,
          sessionId: session.session_id,
          topic: typeof session.topic === 'string' ? session.topic : null,
          topicDescription: session.topic_description || description || null,
          userName: description || null, // Store user's name from description
          completed: false,
          messages: [],
        }));

        service.connect({
          session_id: session.session_id,
          websocket_url: session.websocket_url,
          topic: session.topic,
          topic_description: session.topic_description,
        });
      } catch (err: any) {
        console.error('Failed to start GD session', err);
        onError(err?.message || 'Failed to start Group Discussion');
        throw err;
      }
    },
    [ensureService, onError, onStatus]
  );

  const sendUserText = useCallback((text: string) => {
    if (!text.trim()) return;
    const service = ensureService();
    service.sendUserText(text.trim());
    setState((prev) => ({
      ...prev,
      messages: [
        ...prev.messages,
        {
          speaker: 'user',
          speaker_name: prev.userName || 'You',
          text: text.trim(),
          timestamp: new Date().toISOString(),
        },
      ],
    }));
  }, [ensureService]);

  const sendUserAudio = useCallback((audioData: string) => {
    const service = ensureService();
    service.sendAudioChunk(audioData);
  }, [ensureService]);

  const startRecording = useCallback(() => {
    const service = ensureService();
    service.startRecording();
  }, [ensureService]);

  const stopRecording = useCallback(() => {
    const service = ensureService();
    service.stopRecording();
  }, [ensureService]);

  const stopSession = useCallback(async () => {
    const service = serviceRef.current;
    if (service) {
      service.stop();
    }
    setState(initialState);
  }, []);

  const prepareAudio = useCallback(async () => {
    const service = ensureService();
    await service.prepareAudio();
  }, [ensureService]);

  useEffect(() => {
    return () => {
      serviceRef.current?.cleanup();
      serviceRef.current = null;
    };
  }, []);

  return {
    state,
    startSession,
    sendUserText,
    sendUserAudio,
    startRecording,
    stopRecording,
    stopSession,
    prepareAudio,
    isConnecting: state.connected === false && state.sessionId !== null && !state.reconnecting,
  };
};

export default useGdOrchestration;
